import { css, styled } from '@mui/material/styles';

import type { BaseRow, RowActionButtonBaseProps, RowActionProps, StickyPosition } from '@openx/types/tableHelpers';
import type { JSX } from 'react';
import { IconCell } from './IconCell';
import { MenuButton } from './MenuButton/MenuButton';
import { RowActions } from './RowActions';

const StyledMenuContainerSpan = styled('span')`
  display: flex;
  justify-content: flex-end;
  padding-right: ${({ theme }) => theme.spacing(2.5)};
`;

interface StylesProps {
  hasDropdownActions: boolean;
  hasButtonActionsAvailable: boolean;
}

const StyledIconCell = styled(IconCell)<StylesProps & { sticky?: StickyPosition; stickyRightOffset?: number }>`
  ${({ sticky }) =>
    !sticky &&
    css`
      position: relative;
    `}

  ${({ hasDropdownActions, hasButtonActionsAvailable, sticky }) =>
    !hasDropdownActions &&
    !sticky &&
    css`
      border-left: 0;
      display: ${hasButtonActionsAvailable ? 'table-cell' : 'none'};
      margin: 0;
      padding: 0;
      width: 0;
    `}
`;

export interface ActionsCellProps<RowT extends BaseRow> {
  rowData: RowT;
  primaryAction?: RowActionButtonBaseProps<RowT>;
  secondaryAction?: RowActionButtonBaseProps<RowT>;
  dangerAction?: RowActionButtonBaseProps<RowT>;
  defaultAction?: RowActionButtonBaseProps<RowT>;
  actions?: RowActionProps<RowT>[];
  customRowActions?: RowActionButtonBaseProps<RowT>[];
  sticky?: StickyPosition;
  stickyRightOffset?: number;
  isLeadingStickyRight?: boolean;
}

export function ActionsCell<RowT extends BaseRow>({
  rowData,
  primaryAction,
  secondaryAction,
  dangerAction,
  defaultAction,
  actions,
  customRowActions,
  sticky,
  stickyRightOffset,
  isLeadingStickyRight,
}: ActionsCellProps<RowT>): JSX.Element {
  const hasButtonActionsAvailable = !!(
    primaryAction ||
    secondaryAction ||
    dangerAction ||
    defaultAction ||
    customRowActions
  );
  const hasDropdownActions = !!actions;

  return (
    <StyledIconCell
      stopClickPropagation
      hasButtonActionsAvailable={hasButtonActionsAvailable}
      hasDropdownActions={hasDropdownActions}
      sticky={sticky}
      stickyRightOffset={stickyRightOffset}
      isLeadingStickyRight={isLeadingStickyRight}
    >
      {hasButtonActionsAvailable && (
        <RowActions
          primaryAction={primaryAction}
          secondaryAction={secondaryAction}
          defaultAction={defaultAction}
          dangerAction={dangerAction}
          customRowActions={customRowActions}
          rowData={rowData}
        />
      )}

      {actions && (
        <StyledMenuContainerSpan>
          <MenuButton menuItems={actions} rowData={rowData} data-test="row-actions" />
        </StyledMenuContainerSpan>
      )}
    </StyledIconCell>
  );
}
